import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { checkLoginStatus, loginTraditional, registerUser, logoutUser } from '../services/authService';
import { USER_ROUTES } from '../constants/routeConstants';
import { USER_LOGIN_PAGE } from '../constants/appConstants';
import LoadingSpinner from '../components/LoadingSpinner/LoadingSpinner';

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Check if user is logged in on page load
  const checkAuthStatus = useCallback(async () => {
    try {
      const response = await checkLoginStatus();
      
      if (response.data.authenticated) {
        setUser(response.data.user);
      } else {
        setUser(null);
      }
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Run once when the app mounts
  useEffect(() => {
    checkAuthStatus();
  }, [checkAuthStatus]);

  const login = useCallback(async (email, password, remember) => {
    try {
      const response = await loginTraditional(email, password, remember);

      if (response.data.user) {
        setUser(response.data.user);
        return { success: true, message: response.data.message };
      } else {
        return { success: false, message: "No user data received" };
      }
      
    } catch (error) {
      console.error("Login Error:", error);
      return {
        success: false,
        message: error.response?.data?.error || USER_LOGIN_PAGE.MESSAGES.INVALID_CREDENTIALS
      };
    }
  }, []);

  const register = useCallback(async (name, email, password, confirmPassword) => {
    try {
      const response = await registerUser({ 
        name, 
        email, 
        password, 
        confirm_password: confirmPassword 
      });

      setUser(response.data.user);
      return { success: true, message: response.data.message };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.error || 'Registration failed'
      };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await logoutUser();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setLoading(false);
      setUser(null);
      navigate(USER_ROUTES.LOGIN, { replace: true });
    }
  }, [navigate]);

  const value = useMemo(() => ({
    user,
    loading,
    login,
    register,
    logout,
    checkAuthStatus
  }), [user, loading, login, register, logout, checkAuthStatus]);

  return (
    <AuthContext.Provider value={value}>
      {loading ? (
        <LoadingSpinner text="Preparing your workspace..." fullScreen />
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};
